import { Component, OnInit, HostListener } from '@angular/core';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Router, ActivatedRoute } from '@angular/router';
import * as _ from 'lodash';
import { HttpService } from 'app/shared/services/http.service';
import { SharedService } from 'app/shared/services/shared.service';
import { GridService } from 'app/shared/services/grid.service';
import { DictionaryService } from 'app/shared/services/dictionary.service';
import { UserService } from 'app/shared/services/user.service';
import { ModalsService } from 'app/shared/services/modals.service';
import { GoogleAnalyticsService } from 'app/shared/services/google-analytics.service';

@AutoUnsubscribe()
@Component({
  selector: 'new-publisher',
  templateUrl: './new-publisher.component.html',
  styleUrls: ['./new-publisher.component.scss'],
})
export class NewPublisherComponent implements OnInit {
  public allSections: Array<any>;
  public activeSection: number;
  public editMode: boolean;
  public routeParams: any;
  public publisherData: any;
  public originalData: any;
  public getPublisherResponse: any;
  public assignedPlacementsData: Array<any>;
  public assignedPlacementsColumnsSettings: Array<any>;
  public assignedPlacementsIsLoading: boolean;
  public assignedPlacementsIsError: boolean;
  public assignedPlacementsIdsOnLoad: Array<number>;
  public activeEntities: boolean;
  public reportMeasures: Array<any>;
  public errorInputs: Array<string>;
  public isLoading: boolean;
  public isPublisherIONetwork: boolean;
  public subscriptions: Array<any>;
  public assignedPlacementFiltersToGrid: any;
  public assignedPlacementFilters: any;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public httpService: HttpService,
    public sharedService: SharedService,
    public gridService: GridService,
    public dictionaryService: DictionaryService,
    public userService: UserService,
    public modalsService: ModalsService,
    public ga: GoogleAnalyticsService
  ) {
    this.allSections = [
      { name: 'SETTINGS', value: 'publisherSettings', toggleStatus: true },
      { name: 'PLACEMENTS', value: 'publisherAssignedPlacement', toggleStatus: true },
    ];
    this.publisherData = {
      publisher_name: '',
      publisher_email: '',
      publisher_username: '',
      publisher_password: '',
      report_measures: 2,
      publisher_status: true,
    };
    this.originalData = {};
    this.getPublisherResponse = { publisher_players: [] };
    this.assignedPlacementFilters = { active: [true] };
    this.assignedPlacementFiltersToGrid = _.cloneDeep(this.assignedPlacementFilters);
    this.activeSection = 0;
    this.editMode = false;
    this.routeParams = {};
    this.assignedPlacementsData = [];
    this.assignedPlacementsColumnsSettings = [
      { key: '$$selected', label: 'ASSIGNED' },
      { key: 'active', label: 'ACTIVE' },
      { key: 'playerId', label: 'PLACEMENT_ID', filterType: 'INPUT', primaryKey: true },
      { key: 'name', label: 'PLACEMENT_NAME', filterType: 'INPUT' },
      { key: 'cost_calc', label: 'COST_CALCULATION', rightAligned: true },
      {
        key: 'environment',
        label: 'ENVIRONMENT_SHORT',
        filterType: 'SELECT',
        parseFunction: this.gridService.parseMultipleEnvironments,
        parseTooltipFunction: this.gridService.parseMultipleEnvironmentsTooltip,
      },
      {
        key: 'type',
        label: 'TYPE',
        filterType: 'SELECT',
        parseFunction: this.gridService.parseSupplyType,
        parseTooltipFunction: this.gridService.parseSupplyType,
      },
      { key: 'fixed_cost', label: 'FIXED_COST', rightAligned: true },
      { key: 'revshare_cost', label: 'REVSHARE_COST', rightAligned: true },
    ];
    this.assignedPlacementsIsLoading = false;
    this.assignedPlacementsIsError = false;
    this.assignedPlacementsIdsOnLoad = [];
    this.reportMeasures = [
      { key: 1, val: 'OPP_REVENUE' },
      { key: 2, val: 'IMP_REVENUE' },
      { key: 3, val: 'IMP_OPP_REVENUE' },
    ];
    this.activeEntities = true;
    this.subscriptions = [];
    this.errorInputs = [];
    this.isLoading = false;
    this.isPublisherIONetwork = this.detectPublisherIONetwork();

    this.subscriptions.push(
      this.sharedService.getMessagesBus().subscribe((response: any) => {
        if (response.type === 'NETWORK_CHANGE') {
          this.errorInputs = [];
          this.isPublisherIONetwork = this.detectPublisherIONetwork();
          if (this.editMode && !response.data.forcedChange) {
            this.originalData = _.cloneDeep(this.publisherData);
            this.close();
          } else {
            this.isLoading = true;
            this.getSupplies();
          }
        } else if (response.type === 'FORM_ERROR_HIDE') {
          _.without(this.errorInputs, response.data);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.hasChanges();
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.route.params.subscribe((params) => {
        if (params.id && params.id !== 'new') {
          this.routeParams.id = params.id;
          this.getPublisher();
          this.sharedService.setPageTitle('EDIT_PUBLISHER');
        } else {
          this.getSupplies();
          this.sharedService.setPageTitle('NEW_PUBLISHER');
        }
      })
    );
  }

  hasChanges(): boolean {
    return !_.isEqual(this.publisherData, this.originalData);
  }

  getPublisher(): void {
    this.isLoading = true;
    this.subscriptions.push(
      this.httpService.post('publishers/getPublisher', { publisher_id: this.routeParams.id }).subscribe(
        (response: any) => {
          if (response.publisher_id) {
            this.editMode = true;
            this.getPublisherResponse = _.cloneDeep(response);
            this.getSupplies();
            this.userService.changeNetworkIfNeeded(response);
          } else {
            this.onPublisherNotFound();
          }
        },
        () => {
          // Display the error message
          this.onPublisherNotFound();
        }
      )
    );
  }

  getSupplies(): void {
    const params: any = {};
    if (this.activeEntities !== null) {
      params.activeSupplies = this.activeEntities ? 1 : 0;
    }
    this.isLoading = true;
    this.subscriptions.push(
      this.httpService.post('vdsp_ws/getSupplies', params).subscribe((response: any) => {
        const data: Array<any> = _.cloneDeep(response);
        let currSupplyId: number;
        for (let i = 0; i < data.length; i++) {
          currSupplyId = +data[i].playerId;
          data[i].$$selected =
            _.find(this.getPublisherResponse.publisher_players, (supply: any) => {
              return +supply.playerId === currSupplyId;
            }) !== undefined;
          data[i].environment = {
            is_desktop: +data[i].is_desktop === 1,
            is_mobile_web: +data[i].is_mobile_web === 1,
            is_mobile_app: +data[i].is_mobile_app === 1,
          };
          data[i].fixed_cost = '';
          data[i].revshare_cost = '';
          data[i].cost_calc = '';
          switch (+data[i].cost_type) {
            case 1:
            case 4:
              data[i].revshare_cost = parseFloat(data[i].cost_value);
              data[i].cost_calc = data[i].cost_value + '% ';
              break;
            case 2:
            case 3:
            case 5:
              data[i].fixed_cost = parseFloat(data[i].cost_value);
              data[i].cost_calc = data[i].cost_value + '$ ';
              break;
          }
          data[i].$$actions = [
            { type: 'EDIT', link: true, linkOverride: '/supply/manage/' },
            { type: 'REPORT', link: true, linkOverride: '/reports/new?d=1=' },
          ];
          data[i].$$link = `/supply/manage/${data[i].playerId}`;
        }
        this.assignedPlacementsData = data;
        this.initPublisherData();
      })
    );
  }

  initPublisherData(): void {
    if (this.editMode) {
      this.publisherData = _.cloneDeep(this.getPublisherResponse);
      this.publisherData.publisher_status = +this.publisherData.publisher_status === 1;
      this.publisherData.publisher_hb = +this.publisherData.publisher_hb === 1;
      this.publisherData.report_measures = +this.publisherData.report_measures;
    }
    this.originalData = _.cloneDeep(this.publisherData);
    this.assignedPlacementsIdsOnLoad = _.map(_.filter(this.assignedPlacementsData, '$$selected'), (placement: any) => {
      return +placement.playerId;
    });
    this.isLoading = false;
  }

  onPublisherNotFound(): void {
    this.originalData = _.cloneDeep(this.publisherData);
    this.sharedService.showFlashMessage(this.dictionaryService.get('PUBLISHER_NOT_FOUND'), 'error');
    this.close();
  }

  toggleSection(id: number): void {
    this.allSections[id].toggleStatus = !this.allSections[id].toggleStatus;
  }

  goToSection(id: number): void {
    const section: HTMLElement = document.getElementById(this.allSections[id].value);
    const yPosition: number = id > 0 ? section.offsetTop : 0;
    window.scroll({ top: yPosition, left: 0, behavior: 'smooth' });
    this.activeSection = id;
    if (!this.allSections[id].toggleStatus) {
      this.toggleSection(id);
    }
    setTimeout(() => {
      this.activeSection = id;
    }, 400);
  }

  getSectionsPosition(): number {
    return window.scrollY > 72 ? 0 : 72;
  }

  getSectionsArrowPosition(): number {
    return document.getElementById('sectionButton' + this.activeSection).offsetTop;
  }

  validateForm(): boolean {
    let valid = true;
    this.errorInputs = [];
    this.publisherData.publisher_email = this.publisherData.publisher_email.trim();
    if (this.sharedService.isEmpty(this.publisherData.publisher_name)) {
      valid = false;
      this.errorInputs.push('publisherName');
    }
    if (this.sharedService.isEmpty(this.publisherData.publisher_email)) {
      valid = false;
      this.errorInputs.push('missingEmail');
    } else if (!this.sharedService.validateEmail(this.publisherData.publisher_email)) {
      valid = false;
      this.errorInputs.push('invalidEmail');
    }
    if (!this.editMode && this.sharedService.isEmpty(this.publisherData.publisher_password)) {
      valid = false;
      this.errorInputs.push('missingPassword');
    } else if (
      !(this.editMode && this.sharedService.isEmpty(this.publisherData.publisher_password)) &&
      !this.sharedService.validatePassword(this.publisherData.publisher_password)
    ) {
      valid = false;
      this.errorInputs.push('invalidPassword');
    }
    if (!valid) {
      this.sharedService.onEntitySaveFailure();
    }
    return valid;
  }

  save(close: boolean = false): void {
    const noErrors: boolean = this.validateForm();
    const url: string = this.editMode ? 'updatePublisher' : 'addPublisher';
    let params: any = {};
    let assignedPlacements: Array<any>;
    const assignedPlacementsIds: Array<number> = [];
    let removedPlacements: Array<number> = [];
    if (noErrors) {
      this.isLoading = true;
      assignedPlacements = _.cloneDeep(_.filter(this.assignedPlacementsData, { $$selected: true }));
      for (let i = 0; i < assignedPlacements.length; i++) {
        assignedPlacements[i].id = assignedPlacements[i].playerId;
        assignedPlacementsIds.push(+assignedPlacements[i].id);
        for (const property in assignedPlacements[i]) {
          if (!['id', 'name'].includes(property) && assignedPlacements[i].hasOwnProperty(property)) {
            delete assignedPlacements[i][property];
          }
        }
      }
      removedPlacements = this.assignedPlacementsIdsOnLoad.filter(
        (placement: number) => !assignedPlacementsIds.includes(placement)
      );
      params = {
        publisher_name: this.publisherData.publisher_name,
        publisher_username: this.publisherData.publisher_username,
        publisher_email: this.publisherData.publisher_email,
        publisher_status: this.publisherData.publisher_status ? '1' : '0',
        report_measures: +this.publisherData.report_measures,
        publisher_hb: this.publisherData.publisher_hb ? '1' : '0',
        publisher_players: assignedPlacements,
        removed_placements: removedPlacements,
      };
      if (!this.sharedService.isEmpty(this.publisherData.publisher_password)) {
        params.publisher_password = this.publisherData.publisher_password;
      }
      if (this.editMode) {
        params.publisher_id = this.publisherData.publisher_id;
      }
      this.subscriptions.push(
        this.httpService.post('publishers/' + url, params).subscribe(
          (response: any) => {
            if (response.success) {
              this.onSaveSuccess(response);
            } else if (response.errors) {
              this.onSaveError(response.errors);
            }
            if (close) {
              this.close();
            }
          },
          () => {
            this.sharedService.showErrorFlashMessage();
            this.isLoading = false;
          }
        )
      );
    }
  }

  close(): void {
    this.router.navigate(['/publishers/manage/']).then(() => {
      return;
    });
  }

  saveAndExit(): void {
    this.save(true);
  }

  detectPublisherIONetwork(): boolean {
    return (
      this.userService.networkData &&
      this.userService.networkData.publisher_io &&
      this.userService.getFeatureState('publisher_hb')
    );
  }

  onSaveSuccess(response: any): void {
    const message: string = !this.editMode ? 'PUBLISHER_CREATED_SUCCESSFULLY' : 'PUBLISHER_SAVED_SUCCESSFULLY';
    this.originalData = _.cloneDeep(this.publisherData);
    this.sharedService.showFlashMessage(this.dictionaryService.get(message), 'success');
    this.isLoading = false;
    if (!this.editMode) {
      response.publisherId
        ? this.router.navigate([`/publishers/manage/${response.publisherId}`]).then(() => {
            return;
          })
        : this.close();
    }
  }

  onSaveError(response: any): void {
    const players: Array<any> = _.cloneDeep(response.players);
    delete response.players;
    if (
      _.filter(response, (item: any) => {
        return response[item] !== 'VALID';
      }).length > 0
    ) {
      this.sharedService.showErrorFlashMessage(response);
    }
    if (players) {
      if (players.length > 0) {
        this.sharedService.showErrorFlashMessage(players);
      }
    }
    this.isLoading = false;
  }

  @HostListener('document:wheel', ['$event'])
  onScroll(): void {
    let found = false;
    let index = 0;
    const scrollY: number = window.scrollY;
    const sections: HTMLElement = document.getElementById('sections');
    let currSection: HTMLElement;
    if (sections) {
      while (!found && index < this.allSections.length - 1) {
        currSection = document.getElementById(this.allSections[index].value);
        if (currSection) {
          found = currSection.offsetTop >= scrollY;
        }
        if (!found) {
          index++;
        }
      }
    }
    this.activeSection = index;
  }

  onFilterChange($event: any): void {
    let activeEntities: boolean;
    if ($event.active) {
      activeEntities = $event.active.length === 1 ? $event.active[0] : null;
      if (activeEntities !== this.activeEntities) {
        this.activeEntities = activeEntities;
        this.getSupplies();
      }
    }
  }
}
